import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import EnumCreatorType from "@wallet-manager/node-types/dist/src/postgres/const/CreatorType";
import EnumFreezeRequest from "@wallet-manager/node-types/dist/types/ledger/enums/LedgerFreezeRequest";

import { getLedgerFreezeRequestExport } from "../../../api/merchant";
import { Ipage } from "../../../api/types";
import { AssetMultiSelection } from "../../../components/AssetSelection";
import { ChainSingleSelection } from "../../../components/ChainSelection";
import { useDatePicker } from "../../../components/DatePicker";
import { useAssets, useChains } from "../../../components/FetchConfig";
import GeneralBtn from "../../../components/GeneralBtn";
import { NodeTypesEnumMultiSelection } from "../../../components/GeneralSelection";
import MpTextFieldMultiple from "../../../components/MpTextFieldMultiple";
import {
  Box,
  Container,
  SelectChangeEvent,
} from "../../../components/MuiGenerals";
import TableTab from "../../../components/TableTab";
import { useAlerting, usePermission, useTranslation } from "../../../hooks";
import { FeatureCodes } from "../../../utils/constant";
import {
  displayAmount,
  downloadFiles,
  findChainInfo,
  findDecimalByChainNameAndAsset,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../../utils/helper";
import { DivideLine, genField } from "../../../utils/HelperComp";
import { customSx } from "../../../utils/styling";
import { createZusInstance } from "../../../zustand/store";
import FreezeRequestList from "./FreezeRequestList";

const translatePrefix = "ledgerFreezeRequest";

interface ZusParamsFace {
  chain_id: string | undefined;
  chain_type: number | undefined;
  asset_names: string[];
  statuses: string[];
  client_ids: string[];
  merchant_order_ids: string[];
  frozen_order_ids: string[];
  related_order_ids: string[];
  date_created_from: string;
  date_created_to: string;
  date_last_modified_from: string;
  date_last_modified_to: string;
  pageSize: number;
  page: number;
}

interface Ifields {
  chain_name: string;
  asset_names: string[];
  statuses: string[];
  client_id: string;
  merchant_order_id: string;
  frozen_order_id: string;
  related_order_id: string;
}
const initFields = {
  chain_name: "",
  asset_names: [] as string[],
  statuses: [] as string[],
  client_id: "",
  merchant_order_id: "",
  frozen_order_id: "",
  related_order_id: "",
} as const;
const initZusParams = {
  page: 0,
  pageSize: 20,
} as const;
export const useZusParams = createZusInstance<Ipage & Partial<ZusParamsFace>>(
  initZusParams
);
const Pkey = FeatureCodes.ledger.FreezeRequest;
export const reversedEnumFunction: (
  enumObj: Record<string, string>
) => Record<string, string> = (enumObj) =>
  Object.fromEntries(
    Object.entries(enumObj).map(([key, value]) => [value, key])
  );

export default function FreezeRequest() {
  const zusParams = useZusParams();
  const { alerting } = useAlerting();
  const { t, te, tc } = useTranslation(translatePrefix);
  const { hasPermission } = usePermission();
  const [count, setCount] = useState(0);
  const onExport = async () => {
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      getLedgerFreezeRequestExport(
        { ...zusParams.body, page, pageSize },
        { signal }
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const omitKeys = ["rawData"];
    const res = listMapping("name", rawRes, { t, te, omitKeys });
    const config = {};
    downloadFiles(`Freeze Request`, res, config);
  };
  return (
    <Box sx={customSx.layoutBox}>
      <Container disableGutters maxWidth={false}>
        <FilterBar />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Box sx={customSx.tableExportBar}>
          {hasPermission(Pkey.Export) && (
            <GeneralBtn label="export" onClick={onExport} />
          )}
        </Box>
        <TableTab>
          <FreezeRequestList {...{ listMapping, setCount }} />
        </TableTab>
      </Container>
    </Box>
  );
}

export function FilterBar() {
  const [searchedParams] = useSearchParams();

  const initSearchParams = {
    ...initFields,
    merchant_order_id: searchedParams.get("merchant_order_id") || "",
  };
  const [fields, setFields] = useState<Ifields>(initSearchParams);
  const { t } = useTranslation(translatePrefix);
  const zusParams = useZusParams();
  const { selectChainByPieces } = useChains();
  const { selectAssetNamesByChain } = useAssets();
  const DateObj = {
    CreationTime: useDatePicker(),
    LastModifiedDate: useDatePicker(),
  };
  useEffect(() => {
    if (initSearchParams.merchant_order_id) {
      zusParams.setBody({
        merchant_order_ids: [initSearchParams.merchant_order_id],
      });
    }
  }, []);
  const getParams = () => {
    const { chain_name, asset_names, statuses, ...rest } = fields;
    const transformFieldFromStrToArr = (fieldValue: string) => {
      const rawArr = fieldValue.split(",");
      const fieldValueArr = rawArr
        .filter((item) => item)
        .map((item) => item.trim());
      return fieldValueArr;
    };
    const {
      client_id: client_ids,
      merchant_order_id: merchant_order_ids,
      frozen_order_id: frozen_order_ids,
      related_order_id: related_order_ids,
    } = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => [
        key,
        transformFieldFromStrToArr(value),
      ])
    );

    const { chain_id, chain_type } = selectChainByPieces({ chain_name });

    const { CreationTime, LastModifiedDate } = DateObj;
    const { start: date_created_from, end: date_created_to } = CreationTime;
    const { start: date_last_modified_from, end: date_last_modified_to } =
      LastModifiedDate;
    return {
      ...initZusParams,
      chain_id,
      chain_type,
      asset_names,
      statuses,
      client_ids,
      merchant_order_ids,
      frozen_order_ids,
      related_order_ids,
      date_created_from,
      date_created_to,
      date_last_modified_from,
      date_last_modified_to,
    };
  };

  const apiParams: ZusParamsFace = getParams();
  const onChange =
    (type: keyof Ifields) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const onSearch = () => {
    zusParams.setBody({ ...apiParams, page: 0 });
    zusParams.refetch();
  };

  const onReset = () => {
    setFields(initFields);
    DateObj.CreationTime.clearDate();
  };

  const allAssetNames = selectAssetNamesByChain({
    chain_name: fields.chain_name,
  });

  useEffect(
    () => setFields((f) => ({ ...f, asset_names: [] })),
    [fields.chain_name]
  );

  const F = genField({ t }, [
    ["creation_time", <DateObj.CreationTime.Picker type="dateTime" />],
    ["last_modified_time", <DateObj.LastModifiedDate.Picker type="dateTime" />],
    [
      "chain_name",
      <ChainSingleSelection
        label={t("ph_chain_name")}
        setChoice={(chain_name) => setFields((f) => ({ ...f, chain_name }))}
        choice={fields.chain_name}
      />,
    ],
    [
      "currency",
      <AssetMultiSelection
        label={t("ph_currency")}
        setChoices={(asset_names) => setFields((f) => ({ ...f, asset_names }))}
        choices={fields.asset_names}
        allItems={allAssetNames}
      />,
    ],
    [
      "merchant_order_id",
      <MpTextFieldMultiple
        value={fields.merchant_order_id}
        onChange={onChange("merchant_order_id")}
        label={t("ph_merchant_order_id")}
        count={apiParams.merchant_order_ids.length}
      />,
    ],
    [
      "status",
      <NodeTypesEnumMultiSelection
        label={t("ph_status")}
        onChange={(statuses) => setFields((f) => ({ ...f, statuses }))}
        value={fields.statuses}
        enumData={EnumFreezeRequest}
        isNoSorting
      />,
    ],
    [
      "client_id",
      <MpTextFieldMultiple
        value={fields.client_id}
        onChange={onChange("client_id")}
        label={t("ph_client_id")}
        count={apiParams.client_ids.length}
      />,
    ],
    [
      "frozen_order_id",
      <MpTextFieldMultiple
        value={fields.frozen_order_id}
        onChange={onChange("frozen_order_id")}
        label={t("ph_frozen_order_id")}
        count={apiParams.frozen_order_ids.length}
      />,
    ],
    [
      "related_order_id",
      <MpTextFieldMultiple
        value={fields.related_order_id}
        onChange={onChange("related_order_id")}
        label={t("ph_related_order_id")}
        count={apiParams.related_order_ids.length}
      />,
    ],
  ]);
  return (
    <>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.creation_time}
        {F.last_modified_time}
        {F.chain_name}
        {F.currency}
        {F.status}
        {F.client_id}
        {F.merchant_order_id}
        {F.frozen_order_id}
        {F.related_order_id}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  config?: { omitKeys: string[]; t: any; te: any }
): any[][] => {
  const { omitKeys = [], t, te } = config || {};

  const res = array.map((item: any) => {
    const { name: chain_name } =
      findChainInfo(item.chain_type, item.chain_id) || {};
    const decimals = findDecimalByChainNameAndAsset(
      String(item.chain_type),
      String(item.chain_id),
      String(item.asset_name)
    );
    const mappedResult = [
      ["merchant_order_id", t("merchant_order_id"), item.merchant_order_id],
      ["approvals_required", t("approvals_required"), item.approvals_required],
      ["approvals", t("approvals"), item.approvals],
      ["client_id", t("client_id"), item.client_id],
      ["chain_name", t("chain_name"), chain_name],
      ["currency", t("currency"), item.asset_name],
      ["amount", t("amount"), displayAmount(item.amount, decimals)],
      ["seq", t("seq"), item.seq],
      ["frozen_order_id", t("frozen_order_id"), item.frozen_order_id],
      ["related_order_id", t("related_order_id"), item.related_order_id],
      ["status", t("status"), te(EnumFreezeRequest[item.status])],
      ["created_by", t("created_by"), item.created_by],
      ["creator_type", t("creator_type"), EnumCreatorType[item.creator_type]],
      ["creation_time", t("creation_time"), toDisplayTime(item.created_date)],
      [
        "last_modified_time",
        t("last_modified_time"),
        toDisplayTime(item.last_modified_date),
      ],
      ["remarks", t("remarks"), item.remarks],
      ["error_reason", t("error_reason"), item.error_reason],
      ["rawData", "", item],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });

  const output = res.map(listMappingTransform(which));
  return output;
};
